import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import { Input } from '@redpanda/ui';
import FormFieldWithError from '../../components/forms/FormFieldWithError';
import { toast } from 'react-hot-toast';

interface LoginFormData {
  email: string;
  password: string;
}

const LoginPage: React.FC = () => {
  const { t } = useTranslation(['pages', 'translation']);
  const { storeId } = useParams<{ storeId: string }>();
  const navigate = useNavigate();
  const { login } = useCustomerAuth();
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>();

  const onSubmit = async (data: LoginFormData) => {
    try {
      setSubmitting(true);
      await login(data.email, data.password);
      toast.success(t('pages:booking.login.loginSuccess'));
      navigate(`/book/${storeId}`);
    } catch (error: any) {
      toast.error(error.message || t('pages:booking.login.loginFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 py-8 px-4">
      <div className="container mx-auto max-w-md">
        {/* 返回按鈕 */}
        <button
          onClick={() => navigate(`/book/${storeId}`)}
          className="mb-6 flex items-center space-x-2 text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span>{t('pages:booking.login.backToHome')}</span>
        </button>

        {/* 登入卡片 */}
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-md p-8">
          <div className="text-center mb-8">
            <div className="text-5xl mb-4">🔐</div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">
              {t('pages:booking.login.title')}
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              {t('pages:booking.login.subtitle')}
            </p>
          </div>

          {/* 登入表單 */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Email */}
            <FormFieldWithError
              label={t('pages:booking.login.email')}
              required
              error={errors.email?.message}
              htmlFor="email"
            >
              <Input
                id="email"
                type="email"
                {...register('email', {
                  required: t('translation:validation.emailRequired'),
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: t('translation:validation.emailInvalid'),
                  },
                })}
                variant={errors.email ? 'error' : 'default'}
                disabled={submitting}
              />
            </FormFieldWithError>

            {/* 密碼 */}
            <FormFieldWithError
              label={t('pages:booking.login.password')}
              required
              error={errors.password?.message}
              htmlFor="password"
            >
              <Input
                id="password"
                type="password"
                {...register('password', {
                  required: t('translation:validation.passwordRequired'),
                })}
                variant={errors.password ? 'error' : 'default'}
                disabled={submitting}
              />
            </FormFieldWithError>

            {/* 忘記密碼連結 */}
            <div className="text-right">
              <Link
                to={`/book/${storeId}/forgot-password`}
                className="text-sm text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 transition-colors"
              >
                {t('pages:booking.login.forgotPassword')}
              </Link>
            </div>

            {/* 登入按鈕 */}
            <button
              type="submit"
              disabled={submitting}
              className={`
                w-full px-6 py-3 rounded-lg font-bold text-white transition-all duration-200
                ${
                  submitting
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-brand-600 hover:bg-brand-700 shadow-lg hover:shadow-xl'
                }
              `}
            >
              {submitting ? (
                <span className="flex items-center justify-center space-x-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>{t('pages:booking.login.loggingIn')}</span>
                </span>
              ) : (
                t('pages:booking.login.loginButton')
              )}
            </button>
          </form>

          {/* 註冊連結 */}
          <div className="mt-6 text-center">
            <p className="text-gray-600 dark:text-gray-400">
              {t('pages:booking.login.noAccount')}{' '}
              <Link
                to={`/book/${storeId}/register`}
                className="text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 font-semibold transition-colors"
              >
                {t('pages:booking.login.registerNow')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
